import Tippy from "@tippyjs/react";
import ImageCustom from "../../../../components/custom/image-custom";
import EmptyState from "../../../../components/custom/empty-state";
import { IconEye } from "../../../../components/icons/icon-group";
import { ATTENDANCE_MODE, PAGE_SIZES } from "../../../../constants/status";
import { ATTENDANCE_GRID_CELL_STATUS, AttendanceGridRow, AttendanceRecord } from "../types/attendance";

interface Props {
  dataTable: AttendanceGridRow[];
  loadingTable: boolean;
  fromDate?: string; // DD/MM/YYYY
  toDate?: string; // DD/MM/YYYY
  month?: string; // MM/YYYY (tuỳ chọn)
  page: number;
  limit: number;
  totalRecords: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  onShowModal: (item: AttendanceRecord) => void;
}

interface DateColumn {
  dateStr: string;
  day: number;
  dateFormatted: string;
  label: string;
  weekday: string;
}

const WEEKDAY_LABELS = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

const parseDateString = (dStr: string): Date => {
  if (dStr.includes("/")) {
    const [d, m, y] = dStr.split("/").map(Number);
    return new Date(y, m - 1, d);
  }
  const [y, m, d] = dStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const getDateColumns = (fromDate?: string, toDate?: string, month?: string): DateColumn[] => {
  let start: Date;
  let end: Date;

  if (fromDate && toDate) {
    start = parseDateString(fromDate);
    end = parseDateString(toDate);
  } else if (month) {
    const [mm, yyyy] = month.split("/").map(Number);
    start = new Date(yyyy, mm - 1, 1);
    end = new Date(yyyy, mm, 0);
  } else {
    const now = new Date();
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = now;
  }

  if (start.getTime() > end.getTime()) {
    const tmp = start;
    start = end;
    end = tmp;
  }

  const columns: DateColumn[] = [];
  const isSingleMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const curr = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const stop = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (curr.getTime() <= stop.getTime()) {
    const day = curr.getDate();
    const mm = String(curr.getMonth() + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const yyyy = curr.getFullYear();

    columns.push({
      dateStr: `${yyyy}-${mm}-${dd}`,
      day,
      dateFormatted: `${dd}/${mm}/${yyyy}`,
      label: isSingleMonth ? String(day) : `${dd}/${mm}`,
      weekday: WEEKDAY_LABELS[curr.getDay()],
    });
    curr.setDate(curr.getDate() + 1);
  }

  return columns;
};

// Tra cứu style của 1 ô theo ATTENDANCE_GRID_CELL_STATUS (màu nền + icon ký hiệu).
const CELL_STYLE: Record<
  ATTENDANCE_GRID_CELL_STATUS,
  { className: string; label: string; content: string }
> = {
  [ATTENDANCE_GRID_CELL_STATUS.ON_TIME]: { className: "bg-green-100 text-green-700", label: "Đúng giờ (1 công)", content: "✓" },
  [ATTENDANCE_GRID_CELL_STATUS.LATE_OR_EARLY]: { className: "bg-yellow-100 text-yellow-700", label: "Trễ giờ / Về sớm (nửa công)", content: "!" },
  [ATTENDANCE_GRID_CELL_STATUS.ABSENT_UNPERMITTED]: { className: "bg-red-100 text-red-700", label: "Vắng không phép (0 công)", content: "✕" },
  [ATTENDANCE_GRID_CELL_STATUS.ABSENT_PERMITTED]: { className: "bg-blue-100 text-blue-700", label: "Vắng có phép / Nghỉ lễ", content: "P" },
  [ATTENDANCE_GRID_CELL_STATUS.DAY_OFF]: { className: "text-gray-300", label: "Ngày nghỉ theo lịch/ca", content: "—" },
  [ATTENDANCE_GRID_CELL_STATUS.PENDING_TODAY]: { className: "bg-blue-50 text-blue-600 animate-pulse", label: "Hôm nay — đang làm việc", content: "..." },
  [ATTENDANCE_GRID_CELL_STATUS.FUTURE]: { className: "text-gray-200", label: "Chưa tới ngày", content: "—" },
  [ATTENDANCE_GRID_CELL_STATUS.NOT_APPLICABLE]: { className: "text-gray-200", label: "Không chấm công", content: "" },
  [ATTENDANCE_GRID_CELL_STATUS.NO_ATTENDANCE_FULL_DAY]: { className: "text-green-700", label: "Đủ công (không chấm công)", content: "1" },
  [ATTENDANCE_GRID_CELL_STATUS.NO_ATTENDANCE_HALF_DAY]: { className: "text-yellow-700", label: "Nửa công (không chấm công)", content: "0.5" },
  [ATTENDANCE_GRID_CELL_STATUS.NO_ATTENDANCE_NO_PAY]: { className: "text-red-700", label: "0 công — chưa đạt mốc số đơn", content: "0" },
};

const STICKY_NAME_HEADER_STYLE = { backgroundColor: "#f9fafb" };
const STICKY_NAME_CELL_STYLE = { backgroundColor: "#ffffff" };

const AttendanceManagementGrid = ({
  dataTable,
  loadingTable,
  fromDate,
  toDate,
  month,
  page,
  limit,
  totalRecords,
  setPage,
  setLimit,
  onShowModal,
}: Props) => {
  const dateColumns = getDateColumns(fromDate, toDate, month);
  const totalPages = Math.ceil(totalRecords / limit);

  return (
    <div className="datatables min-h-52">
      <div className="overflow-x-auto border rounded-md" style={{ maxHeight: "70vh" }}>
        <table className="w-full text-sm border-separate whitespace-nowrap" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th
                className="sticky top-0 left-0 z-30 p-3 font-semibold text-left text-gray-700 bg-gray-50 border-r border-b min-w-56"
                style={STICKY_NAME_HEADER_STYLE}
              >
                Tên nhân viên
              </th>
              {dateColumns.map((col) => (
                <th key={col.dateStr} className="sticky top-0 z-10 p-1 font-semibold text-center text-gray-600 bg-gray-50 border-r border-b w-9">
                  <div>{col.label}</div>
                  <div className="text-[10px] font-normal text-gray-400">{col.weekday}</div>
                </th>
              ))}
              <th className="p-3 font-semibold text-center text-gray-700 bg-gray-50 border-b min-w-20">
                Chi tiết
              </th>
            </tr>
          </thead>
          <tbody>
            {loadingTable ? (
              <tr>
                <td colSpan={dateColumns.length + 2} className="p-4 text-center text-gray-500">Đang tải...</td>
              </tr>
            ) : dataTable.length === 0 ? (
              <tr>
                <td colSpan={dateColumns.length + 2}>
                  <EmptyState message="Chưa có dữ liệu" description="Không tìm thấy nhân viên khớp bộ lọc." />
                </td>
              </tr>
            ) : (
              dataTable.map((row) => (
                <tr key={(row.employee as any).id} className="hover:bg-gray-50">
                  <td className="sticky left-0 z-20 p-2 bg-white border-r border-b" style={STICKY_NAME_CELL_STYLE}>
                    <div className="flex items-center gap-2">
                      <div className="border rounded-full shrink-0">
                        <ImageCustom url={row.employee.avatar} className="w-8 h-8 rounded-full" />
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="overflow-hidden text-sm text-ellipsis">{row.employee.name}</span>
                        {row.attendance_mode === ATTENDANCE_MODE.NO_ATTENDANCE && (
                          <span className="text-xs text-gray-400">Không chấm công</span>
                        )}
                        {row.attendance_mode === ATTENDANCE_MODE.PART_TIME && (
                          <span className="text-xs text-purple-600">Partime</span>
                        )}
                      </div>
                    </div>
                  </td>
                  {dateColumns.map((col) => {
                    const cell = row.days[col.dateStr] || row.days[String(col.day)];
                    const style = cell ? CELL_STYLE[cell.status] : CELL_STYLE[ATTENDANCE_GRID_CELL_STATUS.FUTURE];
                    const coefficient = cell?.work_day_coefficient;
                    const hasCoefficient = coefficient !== null && coefficient !== undefined;
                    const content = hasCoefficient ? String(Number(coefficient)) : style.content;
                    const tooltip = [
                      style.label,
                      hasCoefficient ? `${Number(coefficient)} công` : "",
                      cell?.note,
                      cell?.minutes_late ? `Trễ ${cell.minutes_late} phút` : "",
                      cell?.minutes_early ? `Sớm ${cell.minutes_early} phút` : "",
                    ].filter(Boolean).join(" — ");
                    return (
                      <td key={col.dateStr} className="h-full p-0 text-center border-r border-b">
                        <Tippy content={tooltip || style.label}>
                          <div className={`flex items-center justify-center w-full h-full min-h-10 font-bold ${style.className}`}>
                            {content}
                          </div>
                        </Tippy>
                      </td>
                    );
                  })}
                  <td className="p-2 text-center bg-white border-b">
                    <Tippy content="Xem chi tiết">
                      <button
                        type="button"
                        onClick={() =>
                          onShowModal(
                            new AttendanceRecord({
                              date: dateColumns[dateColumns.length - 1]?.dateFormatted || "",
                              employee: row.employee,
                              employee_id: Number((row.employee as any).id),
                            }),
                          )
                        }
                      >
                        <IconEye />
                      </button>
                    </Tippy>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-sm text-gray-500 font-medium">
          Hiển thị {dataTable.length > 0 ? (page - 1) * limit + 1 : 0} -{" "}
          {Math.min(page * limit, totalRecords)} trong tổng số {totalRecords} nhân viên
        </div>

        <div className="flex items-center gap-4">
          <select
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size} hàng
              </option>
            ))}
          </select>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 bg-white"
            >
              &lt;
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .map((p, idx, arr) => {
                const prev = arr[idx - 1];
                const showEllipsis = prev && p - prev > 1;
                return (
                  <span key={p} className="flex items-center">
                    {showEllipsis && <span className="px-1 text-gray-400">...</span>}
                    <button
                      type="button"
                      onClick={() => setPage(p)}
                      className={`min-w-8 h-8 px-2 rounded text-sm font-medium ${
                        p === page
                          ? "bg-green-600 text-white"
                          : "border border-gray-300 hover:bg-gray-100 text-gray-700 bg-white"
                      }`}
                    >
                      {p}
                    </button>
                  </span>
                );
              })}

            <button
              type="button"
              disabled={page >= totalPages || totalPages === 0}
              onClick={() => setPage(page + 1)}
              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 bg-white"
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceManagementGrid;
